import type { BudgetStatusResponse, MonthlySpend } from "@/api";
import { formatUsd } from "@/i18n/format";

export const TREND_MONTHS = 6;
export const BUDGET_WARNING_PERCENT = 80;

export type BudgetLevel = "ok" | "warning" | "blocked";

export function budgetLevel(budget: BudgetStatusResponse): BudgetLevel {
  if (budget.currentSpendUsd >= budget.monthlyLimitUsd) return "blocked";
  if (budget.percentUsed >= BUDGET_WARNING_PERCENT) return "warning";
  return "ok";
}

export function formatCost(value: number): string {
  if (value > 0 && value < 0.01) return `<${formatUsd(0.01)}`;
  return formatUsd(value);
}

const monthKey = (date: Date) => `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}`;

export const utcMonthStart = (now: Date = new Date(), offset = 0) =>
  new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + offset, 1));

export function monthDate(month: string): Date {
  const [year, m] = month.split("-").map(Number);
  return new Date(Date.UTC(year ?? 1970, (m ?? 1) - 1, 1));
}

export function fillMonths(trend: MonthlySpend[], now: Date = new Date(), months = TREND_MONTHS): MonthlySpend[] {
  const byMonth = new Map(trend.map((m) => [m.month, m]));
  return Array.from({ length: months }, (_, i) => {
    const key = monthKey(utcMonthStart(now, -(months - 1 - i)));
    return byMonth.get(key) ?? { month: key, costUsd: 0, requests: 0 };
  });
}

export const FEATURES = [
  "chat",
  "chat_title",
  "agent_routing",
  "agent",
  "group_chat",
  "api",
  "task",
  "task_planning",
  "flow",
  "deep_research",
  "research",
] as const;
export type Feature = (typeof FEATURES)[number];
export const isFeature = (feature: string): feature is Feature => (FEATURES as readonly string[]).includes(feature);
